import { Url } from "../types";

/**
 * Configuration for `createAssetsHttpClient`.
 *
 * Design note — auth and region:
 * The factory is intentionally region-agnostic. The `fetch` function supplied
 * here is responsible for attaching authentication tokens AND any region headers
 * (e.g. the `region` header previously added by `@adsk/acs-sc-web-platform-api`'s
 * `getRegion()`). This keeps the library Node/Lambda-portable: in a Lambda context
 * supply a plain `node-fetch`-based wrapper with appropriate headers; in a browser
 * Forge context supply a wrapper around `fetchWithToken` from the platform API.
 *
 * Design note — relationship service (T001.4.5):
 * `relationshipClient.ts` uses a different backend origin than the Assets service.
 * Create a second `AssetsHttpClient` instance with the relationship-service URL as
 * `baseUrl`:
 *   const assetsHttp = createAssetsHttpClient({ fetch, baseUrl: assetsBackendUrl });
 *   const relHttp    = createAssetsHttpClient({ fetch, baseUrl: relationshipServiceUrl });
 */
export interface AssetsHttpClientConfig {
  /**
   * A fetch-compatible function that handles auth tokens and region headers.
   * Must return a parsed JSON response (i.e. already call `.json()` or equivalent).
   */
  fetch: <T = unknown>(url: Url, init?: RequestInit) => Promise<T>;
  /**
   * Base URL for the backend service this client targets.
   * No trailing slash.
   * Example: "https://developer.api.autodesk.com/construction/assets"
   */
  baseUrl: string;
}

/** The HTTP client returned by `createAssetsHttpClient`. */
export interface AssetsHttpClient {
  /** HTTP GET — returns parsed response body */
  get: <T>(url: Url, init?: Omit<RequestInit, "method">) => Promise<T>;
  /** HTTP POST — returns parsed response body */
  post: <T>(url: Url, init?: Omit<RequestInit, "method">) => Promise<T>;
  /** HTTP PATCH — returns parsed response body */
  patch: <T>(url: Url, init?: Omit<RequestInit, "method">) => Promise<T>;
  /** HTTP PUT — returns parsed response body */
  put: <T>(url: Url, init?: Omit<RequestInit, "method">) => Promise<T>;
  /** HTTP DELETE — returns parsed response body */
  del: <T>(url: Url, init?: Omit<RequestInit, "method">) => Promise<T>;
  /**
   * Returns the base path for a versioned project-scoped endpoint.
   * Equivalent to `getAssetsServiceBasePath(baseUrl, projectId, version)`.
   */
  getAssetsServiceBasePath: (
    projectId: string,
    version?: "v1" | "v2" | "v3"
  ) => string;
  /** The `baseUrl` this client was constructed with (useful for debugging). */
  readonly baseUrl: string;
}

/**
 * Creates an HTTP client for Assets-backend services.
 *
 * @example
 * ```ts
 * // Browser (Forge context):
 * import { fetchWithToken, getRegion } from "@adsk/acs-sc-web-platform-api";
 * const http = createAssetsHttpClient({
 *   fetch: (url, init) =>
 *     fetchWithToken(url, {
 *       ...init,
 *       // Merge init.headers so callers' headers are preserved alongside region.
 *       headers: { region: getRegion(), ...init?.headers },
 *     }),
 *   baseUrl: getEnvValue("ASSETS_BACKEND"),
 * });
 *
 * // Node / Lambda:
 * import nodeFetch from "node-fetch";
 * const http = createAssetsHttpClient({
 *   fetch: (url, init) => nodeFetch(url, init).then((r) => r.json()),
 *   baseUrl: process.env.ASSETS_BACKEND_URL,
 * });
 * ```
 */
export const createAssetsHttpClient = ({
  fetch,
  baseUrl,
}: AssetsHttpClientConfig): AssetsHttpClient => {
  const request = <T>(url: Url, init?: RequestInit): Promise<T> =>
    fetch<T>(url, {
      headers: { "Content-Type": "application/json" },
      ...init,
      // Merge caller-supplied headers on top of the default Content-Type so
      // neither is silently dropped when both are present.
      ...(init?.headers
        ? { headers: { "Content-Type": "application/json", ...init.headers } }
        : {}),
    });

  return {
    get: <T>(url: Url, init?: Omit<RequestInit, "method">) =>
      request<T>(url, init),
    post: <T>(url: Url, init?: Omit<RequestInit, "method">) =>
      request<T>(url, { ...init, method: "POST" }),
    patch: <T>(url: Url, init?: Omit<RequestInit, "method">) =>
      request<T>(url, { ...init, method: "PATCH" }),
    put: <T>(url: Url, init?: Omit<RequestInit, "method">) =>
      request<T>(url, { ...init, method: "PUT" }),
    del: <T>(url: Url, init?: Omit<RequestInit, "method">) =>
      request<T>(url, { ...init, method: "DELETE" }),
    getAssetsServiceBasePath: (projectId, version = "v1") =>
      `${baseUrl}/${version}/projects/${projectId}`,
    baseUrl,
  };
};
